import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FiAlertTriangle } from 'react-icons/fi';
import { passwordChanged, logout } from '../redux/actions/authActions';
import { EMAIL_VERIF_API } from '../config/qbase';
import { emailVerifFetch } from '../services/qbaseClient';
import { useConfirm } from './ConfirmDialog';
import './PasswordChangeModal.css';

const PasswordChangeModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const confirm = useConfirm();
    const [passwords, setPasswords] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.auth);

    useEffect(() => {
        if (!isOpen) {
            // 모달이 닫힐 때 상태 초기화
            setPasswords({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setError('');
            setSuccess('');
        }
    }, [isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPasswords(prev => ({
            ...prev,
            [name]: value
        }));
        
        // 입력 시 에러 메시지 초기화
        if (error) {
            setError('');
        }
        if (success) {
            setSuccess('');
        }
    };

    const validatePasswords = () => {
        if (!passwords.currentPassword.trim()) {
            setError(t('auth:pw.err.currentRequired'));
            return false;
        }
        
        if (!passwords.newPassword.trim()) {
            setError(t('auth:pw.err.newRequired'));
            return false;
        }
        
        if (passwords.newPassword.length < 6) {
            setError(t('auth:pw.err.tooShort'));
            return false;
        }
        
        if (passwords.newPassword !== passwords.confirmPassword) {
            setError(t('auth:pw.err.mismatch'));
            return false;
        }
        
        if (passwords.currentPassword === passwords.newPassword) {
            setError(t('auth:pw.err.sameAsCurrent'));
            return false;
        }
        
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validatePasswords()) {
            return;
        }
        
        setLoading(true);
        setError('');
        setSuccess('');
        
        try {
            const requestData = {
                email: user.email,
                currentPassword: passwords.currentPassword.trim(),
                newPassword: passwords.newPassword.trim()
            };
            
            console.log('🔍 [Password Change Debug] 요청 데이터:', requestData);
            console.log('🔍 [Password Change Debug] 현재 사용자 객체:', user);
            
            const response = await emailVerifFetch(EMAIL_VERIF_API.changePassword, requestData);

            const result = await response.json();
            
            console.log('🔍 [Password Change Debug] 서버 응답:', {
                status: response.status,
                ok: response.ok,
                result: result
            });
            
            if (response.ok && result.success) {
                console.log('✅ 패스워드 변경 성공:', result);

                // 테넌트 계정(VW_QBASE_USER) 반영은 **서버가 한다** (EmailVerifService.syncTenantPassword).
                // 예전에는 여기서 한 번 더 저장했는데, 같은 불변식을 지키는 곳이 둘이 되면
                // 임시 비밀번호 발급처럼 **세션이 없어 클라이언트가 못 부르는 경로**에서 조용히 빠진다.
                setSuccess(t('auth:pw.ok'));

                // Redux 상태 업데이트
                dispatch(passwordChanged());
                
                // 2초 후 모달 닫기
                setTimeout(() => {
                    onClose();
                }, 2000);
                
            } else {
                console.error('❌ 패스워드 변경 실패:', result);
                setError(result.error || result.message || t('auth:pw.err.failed'));
            }
        } catch (error) {
            console.error('패스워드 변경 오류:', error);
            setError(t('auth:pw.err.unexpected'));
        } finally {
            setLoading(false);
        }
    };

    // 임시패스워드 강제 변경 모드에서는 모달을 닫을 수 없음
    const isForceChangeMode = useSelector(state => state.auth.isTemporaryPassword);

    const handleLogout = async () => {
        const ok = await confirm({
            title: t('auth:pw.logout'),
            message: t('auth:pw.logoutConfirm'),
            confirmText: t('auth:pw.logout'),
            cancelText: t('auth:pw.cancel'),
            variant: 'danger'
        });
        if (ok) {
            dispatch(logout());
        }
    };

    if (!isOpen) return null;

    return (
        <div className="password-modal-overlay">
            <div className="password-modal">
                <div className="password-modal-header">
                    <h2 className="password-modal-title">
                        {isForceChangeMode ? t('auth:pw.required') : t('auth:pw.title')}
                    </h2>
                    {isForceChangeMode && (
                        <p className="password-modal-warning">
                            <FiAlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{t('auth:pw.tempInUse')}
                        </p>
                    )}
                    {!isForceChangeMode && (
                        <button
                            type="button"
                            className="password-modal-close"
                            onClick={onClose}
                            aria-label={t('auth:pw.close')}
                        >
                            ×
                        </button>
                    )}
                </div>

                <form className="password-modal-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="currentPassword" className="form-label">
                            {t('auth:pw.current')}
                        </label>
                        <input
                            type="password"
                            id="currentPassword"
                            name="currentPassword"
                            value={passwords.currentPassword}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder={t('auth:pw.currentPlaceholder')}
                            disabled={loading}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="newPassword" className="form-label">
                            {t('auth:pw.new')}
                        </label>
                        <input
                            type="password"
                            id="newPassword"
                            name="newPassword"
                            value={passwords.newPassword}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder={t('auth:pw.newPlaceholder')}
                            disabled={loading}
                            required
                            minLength={6}
                        />
                        <div className="password-hint">
                            <small>{t('auth:pw.minHint')}</small>
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="confirmPassword" className="form-label">
                            {t('auth:pw.confirm')}
                        </label>
                        <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={passwords.confirmPassword}
                            onChange={handleInputChange}
                            className="form-input"
                            placeholder={t('auth:pw.confirmPlaceholder')}
                            disabled={loading}
                            required
                        />
                    </div>

                    {error && (
                        <div className="error-message">
                            <div className="error-icon"><FiAlertTriangle size={14} /></div>
                            <div className="error-text">{error}</div>
                        </div>
                    )}

                    {success && (
                        <div className="success-message">
                            <div className="success-icon">✅</div>
                            <div className="success-text">{success}</div>
                        </div>
                    )}

                    <div className="password-modal-buttons">
                        <button
                            type="submit"
                            className="password-button primary"
                            disabled={loading || !passwords.currentPassword.trim() || !passwords.newPassword.trim() || !passwords.confirmPassword.trim()}
                        >
                            {loading ? t('auth:pw.changing') : t('auth:pw.title')}
                        </button>
                        
                        {!isForceChangeMode && (
                            <button
                                type="button"
                                className="password-button secondary"
                                onClick={onClose}
                                disabled={loading}
                            >
                                {t('auth:pw.cancel')}
                            </button>
                        )}
                        
                        {isForceChangeMode && (
                            <button
                                type="button"
                                className="password-button logout"
                                onClick={handleLogout}
                                disabled={loading}
                            >
                                {t('auth:pw.logout')}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PasswordChangeModal;